import { useQuery } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";
import type { ItsmProviderFamily } from "@/lib/types/itsm-config";

// Metadata dropdown options served by itsm-api via the backoffice proxy
// (ENG-5073). value = stored in config, label = shown in the UI.

export interface MetadataOption {
  value: string;
  label: string;
}
export interface FieldOption {
  value: string;
  label: string;
  allowed_values?: MetadataOption[] | null;
}

const BASE = "/proxy/itsm-api/api/v1/metadata";
/** Not gateway-exposed; reachable only through the backoffice proxy. */
const INTERNAL_BASE = "/proxy/itsm-api/api/v1/internal/metadata";

interface OptionsResponse {
  options: MetadataOption[];
}
interface FieldsResponse {
  fields: FieldOption[];
}

/** These endpoints only serve the real ITSM providers — project-management
 *  metadata is served by service-desk instead, see use-asana-metadata. */
type Family = ItsmProviderFamily;

function metaQuery<T>(
  key: unknown[],
  path: string,
  tenantId: string | undefined,
  integrationId: string | undefined,
  extraEnabled = true,
  base = BASE,
) {
  return {
    queryKey: ["itsm-metadata", ...key, path, tenantId, integrationId, base],
    queryFn: async (): Promise<T> => {
      const { data } = await backofficeClient.get<T>(`${base}${path}`, {
        params: { tenant_id: tenantId, integration_id: integrationId },
      });
      return data;
    },
    enabled: Boolean(tenantId) && Boolean(integrationId) && extraEnabled,
    staleTime: 5 * 60 * 1000, // metadata rarely changes within a session
  };
}

// ---- Jira ----
export function useJiraProjects(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(["jira-projects"], "/jira/projects", t, i),
  );
}
export function useJiraPriorities(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(["jira-priorities"], "/jira/priorities", t, i),
  );
}
export function useJiraStatuses(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(["jira-statuses"], "/jira/statuses", t, i),
  );
}
export function useJiraIssueTypes(t?: string, i?: string, projectKey?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(
      ["jira-issue-types", projectKey],
      `/jira/projects/${encodeURIComponent(projectKey ?? "")}/issue-types`,
      t,
      i,
      Boolean(projectKey),
    ),
  );
}
export function useJiraFields(
  t?: string,
  i?: string,
  projectKey?: string,
  issueTypeId?: string,
) {
  return useQuery(
    metaQuery<FieldsResponse>(
      ["jira-fields", projectKey, issueTypeId],
      `/jira/projects/${encodeURIComponent(projectKey ?? "")}/issue-types/${encodeURIComponent(issueTypeId ?? "")}/fields`,
      t,
      i,
      Boolean(projectKey) && Boolean(issueTypeId),
    ),
  );
}

export interface JiraSiteField {
  value: string;
  label: string;
  /** Jira schema type; an array as "array:<items>", e.g. "array:option". */
  field_type?: string | null;
  custom: boolean;
}
interface JiraSiteFieldsResponse {
  fields: JiraSiteField[];
}

/** Every field on the credential's Jira site, for field mappings. One read
 *  per site rather than per project: a field id names one field site-wide. */
export function useJiraSiteFields(t?: string, i?: string) {
  return useQuery({
    ...metaQuery<JiraSiteFieldsResponse>(
      ["jira-site-fields"],
      "/jira/fields",
      t,
      i,
      true,
      INTERNAL_BASE,
    ),
    retry: false,
  });
}

// ---- JSM ----
export interface JsmCustomField {
  field_id: string;
  name: string;
  request_types: string[];
  /** Declared on a portal request type. Always false for Jira Software. */
  in_request_form: boolean;
  /** How many of the sampled issues carry a value for this field. */
  issues_with_value: number;
  /** The field is on a create screen of one of the project's issue types.
   *  False means it only turned up in sampled ticket data. Absent from older
   *  backends, which means true. */
  on_create_screen?: boolean;
  /** Shown at the top level of a sampled request type's agent issue view.
   *  Absent from older backends, which means false. */
  in_issue_view?: boolean;
  /** The issue-view tab or group holding the field, when the issue view
   *  shows it only there. Absent or null otherwise. */
  issue_view_section?: string | null;
}
export interface JsmCustomFieldsResponse {
  sampled_issues: number;
  /** Already ordered by the API: request-form fields first, then issue-view
   *  fields in layout order, then by usage. */
  fields: JsmCustomField[];
  /** Some portal/Forms/issue-layout lookups failed or the time budget ran
   *  out, so `in_request_form` and `in_issue_view` may be under-reported and
   *  fields may be missing.
   *  Absent from older backends, which means false. */
  lookups_incomplete?: boolean;
  /** Jira returned no issue types for the project, so the credential cannot
   *  read its create screens and every field was found by sampling alone —
   *  `on_create_screen` is then false for all of them. Implies
   *  `lookups_incomplete`. Absent from older backends, which means false. */
  create_screens_unavailable?: boolean;
}

/** Custom fields discovered by sampling the project's most recent issues.
 *  Each discovery costs ~100+ Jira API calls against the tenant's quota, so it
 *  never refetches or retries on its own, since the server already retries
 *  Jira 429/5xx; only an explicit `refetch` re-runs it. */
export function useJsmCustomFields(
  t?: string,
  i?: string,
  projectKey?: string,
) {
  return useQuery({
    ...metaQuery<JsmCustomFieldsResponse>(
      ["jsm-custom-fields", projectKey],
      `/jsm/projects/${encodeURIComponent(projectKey ?? "")}/custom-fields`,
      t,
      i,
      Boolean(projectKey),
      INTERNAL_BASE,
    ),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

// ---- ServiceNow ----
export function useSnowAssignmentGroups(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(
      ["snow-groups"],
      "/servicenow/assignment-groups",
      t,
      i,
    ),
  );
}
export function useSnowTables(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(["snow-tables"], "/servicenow/tables", t, i),
  );
}
export function useSnowTicketTypes(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(
      ["snow-ticket-types"],
      "/servicenow/ticket-types",
      t,
      i,
    ),
  );
}
export function useSnowFields(t?: string, i?: string, table?: string) {
  return useQuery(
    metaQuery<FieldsResponse>(
      ["snow-fields", table],
      `/servicenow/tables/${encodeURIComponent(table ?? "")}/fields`,
      t,
      i,
      Boolean(table),
    ),
  );
}
export interface MandatoryFieldSource {
  kind: "dictionary" | "ui_policy";
  ui_policy_sys_id?: string | null;
  short_description?: string | null;
  conditions?: string | null;
}
export interface MandatoryFieldMeta {
  element: string;
  label: string;
  type: string;
  reference?: string | null;
  default_value?: string | null;
  choices: MetadataOption[];
  sources: MandatoryFieldSource[];
}
interface MandatoryFieldsResponse {
  table: string;
  fields: MandatoryFieldMeta[];
}

/** Fields ServiceNow makes (or can make) mandatory on the table: dictionary
 *  mandatory plus every field a default-form UI policy can require. Reads the
 *  instance's policies and a choice list per field, so it is heavier than the
 *  other lookups and is not retried on its own. */
export function useSnowMandatoryFields(t?: string, i?: string, table?: string) {
  return useQuery({
    ...metaQuery<MandatoryFieldsResponse>(
      ["snow-mandatory-fields", table],
      `/servicenow/tables/${encodeURIComponent(table ?? "")}/mandatory-fields`,
      t,
      i,
      Boolean(table),
      INTERNAL_BASE,
    ),
    retry: false,
    refetchOnWindowFocus: false,
  });
}
export function useSnowChoices(
  t?: string,
  i?: string,
  table?: string,
  field?: string,
) {
  return useQuery({
    queryKey: ["itsm-metadata", "snow-choices", table, field, t, i],
    queryFn: async (): Promise<OptionsResponse> => {
      const { data } = await backofficeClient.get<OptionsResponse>(
        `${BASE}/servicenow/tables/${encodeURIComponent(table ?? "")}/choices`,
        { params: { tenant_id: t, integration_id: i, field } },
      );
      return data;
    },
    enabled: Boolean(t) && Boolean(i) && Boolean(table) && Boolean(field),
    staleTime: 5 * 60 * 1000,
  });
}

// ---- Freshservice ----
interface FreshserviceGroupsResponse extends OptionsResponse {
  /** Freshservice denied /api/v2/groups for this API key, so the names came
   *  off the ticket form and can be missing groups kept off it.
   *  Absent from older backends, which means false. */
  from_ticket_form?: boolean;
}

/** An API key without group access 403s here, and no retry will change that,
 *  so fail on the first attempt instead of sitting on "Loading..." through
 *  the default backoff. */
export function useFreshserviceGroups(t?: string, i?: string) {
  return useQuery({
    ...metaQuery<FreshserviceGroupsResponse>(
      ["fs-groups"],
      "/freshservice/groups",
      t,
      i,
      true,
      INTERNAL_BASE,
    ),
    retry: false,
  });
}

export function useFreshserviceWorkspaces(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(
      ["fs-workspaces"],
      "/freshservice/workspaces",
      t,
      i,
    ),
  );
}
export function useFreshserviceTicketTypes(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(
      ["fs-ticket-types"],
      "/freshservice/ticket-types",
      t,
      i,
    ),
  );
}
export function useFreshserviceStatuses(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(["fs-statuses"], "/freshservice/statuses", t, i),
  );
}
export function useFreshservicePriorities(t?: string, i?: string) {
  return useQuery(
    metaQuery<OptionsResponse>(
      ["fs-priorities"],
      "/freshservice/priorities",
      t,
      i,
    ),
  );
}

/** External priority/status options for the mapping editors, by family. */
export function useExternalPriorityOptions(
  family: Family,
  t?: string,
  i?: string,
): MetadataOption[] {
  const jira = useJiraPriorities(family === "jira" ? t : undefined, i);
  const fs = useFreshservicePriorities(
    family === "freshservice" ? t : undefined,
    i,
  );
  // ServiceNow priorities come from the incident 'priority' choice list.
  const snow = useSnowChoices(
    family === "servicenow" ? t : undefined,
    i,
    "incident",
    "priority",
  );
  if (family === "jira") return jira.data?.options ?? [];
  if (family === "freshservice") return fs.data?.options ?? [];
  return snow.data?.options ?? [];
}

export function useExternalStatusOptions(
  family: Family,
  t?: string,
  i?: string,
): MetadataOption[] {
  const jira = useJiraStatuses(family === "jira" ? t : undefined, i);
  const fs = useFreshserviceStatuses(
    family === "freshservice" ? t : undefined,
    i,
  );
  const snow = useSnowChoices(
    family === "servicenow" ? t : undefined,
    i,
    "incident",
    "state",
  );
  if (family === "jira") return jira.data?.options ?? [];
  if (family === "freshservice") return fs.data?.options ?? [];
  return snow.data?.options ?? [];
}
